import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";
import api from "@/lib/api";

const sel = "h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm text-slate-700 w-full";

function F({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}

// Reusable From/To date + Company→OpCo→Region→RSO→AO→Dealer cascading filter.
export default function OrgDateFilter({ value, onChange, showDealer = true, children }) {
  const { data: opts } = useQuery({
    queryKey: ["rep-opts"],
    queryFn: async () => (await api.get("/reports/filter-options")).data,
  });
  const v = value;
  const setOpco = (x) => onChange({ ...v, opco_id: x, region_id: "", rso_id: "", ao_id: "", dealer_id: "" });
  const setRegion = (x) => onChange({ ...v, region_id: x, rso_id: "", ao_id: "", dealer_id: "" });
  const setRso = (x) => onChange({ ...v, rso_id: x, ao_id: "", dealer_id: "" });
  const setAo = (x) => onChange({ ...v, ao_id: x, dealer_id: "" });

  const regions = (opts?.regions || []).filter((r) => !v.opco_id || r.opco_id === v.opco_id);
  const rsos = (opts?.rsos || []).filter((r) => (!v.opco_id || r.opco_id === v.opco_id) && (!v.region_id || r.region_id === v.region_id));
  const aos = (opts?.aos || []).filter((a) => (!v.region_id || a.region_id === v.region_id) && (!v.rso_id || a.rso_id === v.rso_id));
  const dealers = (opts?.dealers || []).filter((d) => (!v.opco_id || d.opco_id === v.opco_id) && (!v.region_id || d.region_id === v.region_id) && (!v.rso_id || d.rso_id === v.rso_id) && (!v.ao_id || d.ao_id === v.ao_id));

  const active = Object.values(v).filter(Boolean).length;
  const clear = () => {
    const cleared = {};
    Object.keys(v).forEach((k) => { cleared[k] = ""; });
    onChange(cleared);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-4" data-testid="org-date-filter">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Filter size={15} /> Filters {active > 0 && <span className="text-xs text-[#00529B]">({active} active)</span>}
        </div>
        {active > 0 && (
          <Button variant="ghost" size="sm" className="gap-1 text-slate-500" data-testid="filter-clear" onClick={clear}>
            <X size={14} /> Clear
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        <F label="From Date"><Input type="date" data-testid="filter-from" className="h-9" value={v.from_date || ""} onChange={(e) => onChange({ ...v, from_date: e.target.value })} /></F>
        <F label="To Date"><Input type="date" data-testid="filter-to" className="h-9" value={v.to_date || ""} onChange={(e) => onChange({ ...v, to_date: e.target.value })} /></F>
        <F label="OpCo">
          <select className={sel} data-testid="filter-opco" value={v.opco_id || ""} onChange={(e) => setOpco(e.target.value)}>
            <option value="">All OpCos</option>
            {(opts?.opcos || []).map((o) => <option key={o.id} value={o.id}>{o.code}</option>)}
          </select>
        </F>
        <F label="Region">
          <select className={sel} data-testid="filter-region" value={v.region_id || ""} onChange={(e) => setRegion(e.target.value)}>
            <option value="">All Regions</option>
            {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </F>
        <F label="RSO">
          <select className={sel} data-testid="filter-rso" value={v.rso_id || ""} onChange={(e) => setRso(e.target.value)}>
            <option value="">All RSOs</option>
            {rsos.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </F>
        <F label="AO">
          <select className={sel} data-testid="filter-ao" value={v.ao_id || ""} onChange={(e) => setAo(e.target.value)}>
            <option value="">All AOs</option>
            {aos.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </F>
        {showDealer && (
          <F label="Dealer">
            <select className={sel} data-testid="filter-dealer" value={v.dealer_id || ""} onChange={(e) => onChange({ ...v, dealer_id: e.target.value })}>
              <option value="">All Dealers</option>
              {dealers.slice(0, 300).map((d) => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </F>
        )}
        {children}
      </div>
    </div>
  );
}

export { F as FilterField };
